// The failure twin of EmptyState — same panel, solid danger stroke.
//
// IT NEVER RENDERS A STACK TRACE OR A RAW EXCEPTION STRING. That is not a
// style preference: a customer-facing screen leaking `at
// Object.<anonymous> (/opt/maisight/backend/...)` hands out the server's
// filesystem layout, and it tells the reader nothing they can act on.
//
// The guard is structural rather than a convention someone must remember.
// `detail` takes only a string (never an `Error`, never `unknown`), and
// `humanDetail` keeps just the first line and drops even that when it looks
// like a stack frame or a source location. A screen that passes
// `String(err)` therefore degrades to the headline instead of leaking.

import "./ui.css";
import type { ReactNode } from "react";
import { Icon } from "./Icon";

/** First line only, and nothing that looks like a stack frame or a file:line. */
export function humanDetail(detail: string): string | null {
  const first = detail.split("\n")[0].trim();
  if (!first) return null;
  if (/^at\s/.test(first)) return null;
  if (/\.(tsx?|jsx?|mjs|py):\d+/.test(first)) return null;
  if (/^[A-Za-z]*Error:/.test(first)) return null;
  return first;
}

export type ErrorStateProps = {
  headline: string;
  /** Human-readable only. Anything stack-shaped is dropped, not rendered. */
  detail?: string;
  /** Usually a "Try again" <Button>. */
  action?: ReactNode;
};

export function ErrorState({ headline, detail, action }: ErrorStateProps) {
  const safe = detail ? humanDetail(detail) : null;
  return (
    <div className="maisight-ui-state" data-kind="error" role="alert">
      <span className="maisight-ui-state-icon">
        <Icon name="alert-triangle" size={24} />
      </span>
      <h2 className="maisight-ui-state-headline type-h2">{headline}</h2>
      {safe ? <p className="maisight-ui-state-body type-bodyS">{safe}</p> : null}
      {action ? <div className="maisight-ui-state-action">{action}</div> : null}
    </div>
  );
}
